/* eslint-disable @typescript-eslint/no-explicit-any */
import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnChanges, Output, signal, SimpleChanges } from '@angular/core';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { COUNTRY_TO_CURRENCY_MAP } from '../../../../shared/constants/country-currency.constant';
import { CompanyClient } from '../../../../shared';
import { SubscriptionService } from '../../../../dashboard/services';

declare let bootstrap: any;

@Component({
   selector: 'kt-app-invoice-form-company',
   standalone: true,
   changeDetection: ChangeDetectionStrategy.OnPush,
   imports: [CommonModule, ReactiveFormsModule],
   templateUrl: './invoice-form-company.component.html',
   styleUrl: './invoice-form-company.component.scss'
})
export class InvoiceFormCompanyComponent implements OnChanges, AfterViewInit {
   @Input() invoiceForm!: FormGroup;
   @Input() companies: CompanyClient[] = [];
   @Output() openPremiumModalEvent = new EventEmitter<{ type: 'client' | 'company'; count: number }>();

   private subscriptionService = inject(SubscriptionService);

   canAddCompany = this.subscriptionService.canAddCompany(this.companies.length);
   showCompanyForm = signal(false);

   countryToCurrencyMap: Record<string, string> = COUNTRY_TO_CURRENCY_MAP;
   enableGSTHSTInputs = false;

   get countries(): string[] {
      return Object.keys(this.countryToCurrencyMap);
   }

   ngOnChanges(changes: SimpleChanges): void {
      if (changes['companies']) {
         this.canAddCompany =
            this.subscriptionService.canAddCompany(this.companies.length);

         if (!this.canAddCompany && this.companies.length > 0) {
            this.validateAndUpdateForm();
         }
      }
   }

   private validateAndUpdateForm(): void {
      if (!this.canAddCompany && this.companies.length > 0) {
         const company: CompanyClient = this.companies.filter(c => c.isPrimary)[0];

         this.populateCompanyForm(company);
      }
   }

   onCompanySelect(event: Event): void {
      const selectElement = event.target as HTMLSelectElement;
      const companyId = selectElement.value;

      const selectedCompany = this.companies.find(c => c.id === companyId);

      if (selectedCompany) {
         this.populateCompanyForm(selectedCompany);
         this.showCompanyForm.set(true);
      } else {
         this.resetCompanyFields();
         this.showCompanyForm.set(false);
      }
   }

   private populateCompanyForm(company: CompanyClient) {
      this.invoiceForm.patchValue({
         companyName: company.name,
         companyAddress: company.address.address,
         companyCity: company.address.city,
         companyProvince: company.address.province,
         companyPostalCode: company.address.postalCode,
         companyCountry: company.address.country,
         companyPhone: company.phone,
         companyLogo: company.logoId,
         companyCurrency: company.address.countryCurrency,
         taxEnabled: company.taxEnabled,
         gstNumber: company.gstNumber,
         hstNumber: company.hstNumber,
         websiteLink: company.websiteLink
      });

      this.setCompanyFieldsEnabled(false);
   }

   resetCompanyFields() {
      this.invoiceForm.patchValue({
         companyName: '',
         companyAddress: '',
         companyCity: '',
         companyProvince: '',
         companyPostalCode: '',
         companyCountry: '',
         companyPhone: '',
         companyLogo: null,
         companyCurrency: '',
         taxEnabled: false,
         gstNumber: '',
         hstNumber: '',
         websiteLink: ''
      });

      this.setCompanyFieldsEnabled(true);
   }

   private setCompanyFieldsEnabled(enable: boolean): void {
      const companyControls = [
         'companyName',
         'companyAddress',
         'companyCity',
         'companyProvince',
         'companyPostalCode',
         'companyCountry',
         'companyPhone',
         'companyLogo',
         'companyCurrency',
         'taxEnabled',
         'gstNumber',
         'hstNumber',
         'websiteLink'
      ];

      companyControls.forEach(ctrlName => {
         const control = this.invoiceForm.get(ctrlName);
         if (control) {
            if (enable) {
               control.enable({ emitEvent: false });
            } else {
               control.disable({ emitEvent: false });
            }
         }
      });
   }

   onCompanyCountryChange(selectedCountry: string): void {
      const currency = this.countryToCurrencyMap[selectedCountry];
      if (currency) {
         this.invoiceForm.patchValue({ companyCurrency: currency });
      }
   }

   addCompanyDetails() {
      this.showCompanyForm.set(true);
   }

   toggleGSTHSTInput(): void {
      this.enableGSTHSTInputs = !this.enableGSTHSTInputs;

      this.setFormControlState(['gstNumber', 'hstNumber'], this.enableGSTHSTInputs, Validators.required);
   }

   private setFormControlState(controlNames: string[], enabled: boolean, validator: any): void {
      controlNames.forEach(name => {
         const ctrl = this.invoiceForm.get(name);
         if (!ctrl) return;

         if (enabled) {
            ctrl.setValidators([validator]);
            ctrl.enable();
         } else {
            ctrl.clearValidators();
            ctrl.disable();
         }

         ctrl.updateValueAndValidity();
      });
   }

   onPremiumAddCompanyClick(): void {
      this.openPremiumModalEvent.emit({ type: 'company', count: this.companies.length });
   }

   ngAfterViewInit() {
      const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
      tooltipTriggerList.forEach(el => {
         new bootstrap.Tooltip(el);
      });
   }
}